/**
 * Call Center floating operator widget (chat-style bottom panel).
 * Subscribes to CallCenter::OperatorChannel for ringing; accepts via LiveKit.
 */

import { h, render } from 'preact'
import { useEffect, useState, useRef, useCallback, useMemo } from 'preact/hooks'
import { ConnectionState, getLogger, LoggerNames, ParticipantEvent, Room, RoomEvent, Track } from 'livekit-client'
import { subscribe } from '../../../lib/cable'
import { setWidgetSlot } from '../../../lib/widget_manager.js'
import { useWidgetLayout } from '../../../lib/use_widget_layout.js'

const RINGTONE_URL = '/sounds/ring.wav'

/**
 * Browser AEC/NS + Chrome voiceIsolation.
 * Network echo from SIP still needs half-duplex ducking (below).
 */
const MIC_CAPTURE = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  voiceIsolation: true
}

/** Mute remote while operator speaks; hold covers SIP round-trip echo. */
const ECHO_DUCK_VOLUME = 0
const ECHO_HOLD_MS = 900
/** LiveKit audioLevel (0–1); low so ducking starts before isSpeaking flips. */
const ECHO_LEVEL_ATTACK = 0.015

// IVR agent leave / room teardown races close the publisher data channel while the
// PeerConnection is still "connected" — livekit-client logs that as error. Harmless
// after transfer leave-room-open; suppress so operators are not misled.
const BENIGN_LIVEKIT_LOG = /publisher data channel .+ closed unexpectedly/i

function silenceBenignLivekitConsoleNoise() {
  ;[LoggerNames.Default, LoggerNames.Engine, LoggerNames.Room].forEach((name) => {
    const log = getLogger(name)
    const originalFactory = log.methodFactory
    log.methodFactory = (methodName, level, loggerName) => {
      const raw = originalFactory(methodName, level, loggerName)
      return (msg, context) => {
        if (methodName === 'error' && typeof msg === 'string' && BENIGN_LIVEKIT_LOG.test(msg)) {
          return
        }
        if (context !== undefined) raw(msg, context)
        else raw(msg)
      }
    }
    log.setLevel(log.getLevel())
  })
}

silenceBenignLivekitConsoleNoise()

let ringtoneAudio = null

function playRingtone() {
  try {
    if (!ringtoneAudio) {
      ringtoneAudio = new Audio(RINGTONE_URL)
      ringtoneAudio.loop = true
      ringtoneAudio.volume = 0.6
    }
    ringtoneAudio.currentTime = 0
    ringtoneAudio.play().catch(() => { /* autoplay blocked — ignore */ })
  } catch { /* Audio unsupported */ }
}

function stopRingtone() {
  if (!ringtoneAudio) return
  try {
    ringtoneAudio.pause()
    ringtoneAudio.currentTime = 0
  } catch { /* ignore */ }
}

function sipCallStatus(participant) {
  return String(participant?.attributes?.['sip.callStatus'] || '').toLowerCase()
}

/** LiveKit SIP: dialing/ringing until PSTN 200 OK, then active. */
function sipCalleeAnswered(participant) {
  const status = sipCallStatus(participant)
  return status === 'active' || status === 'connected'
}

const t = (key, fallback) => {
  const keys = key.split('.')
  let value = window.I18n?.call_center
  for (const k of keys) {
    value = value?.[k]
    if (value == null) break
  }
  return typeof value === 'string' ? value : (fallback || key)
}

/** Map transcript roles to short operator-facing labels. */
function roleLabel(role) {
  const r = String(role || '').toLowerCase()
  if (r === 'assistant' || r === 'ai' || r === 'model' || r === 'agent') {
    return t('widget.role_ai', 'AI')
  }
  if (r === 'caller' || r === 'user') {
    return t('widget.role_caller', 'Caller')
  }
  if (r === 'system') {
    return t('widget.role_system', 'System')
  }
  return role || t('widget.role_unknown', 'Unknown')
}

/** Drop consecutive duplicate transcript lines (IVR say + conversation_item_added). */
function uniqueTranscript(lines) {
  if (!Array.isArray(lines)) return []
  const out = []
  for (const line of lines) {
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.role === line.role &&
      String(prev.content || '').trim() === String(line.content || '').trim()
    ) {
      continue
    }
    out.push(line)
  }
  return out
}

function apiBase(el) {
  return el?.dataset?.callCenterWidgetApiBaseValue ||
    el?.getAttribute('data-call-center-widget-api-base-value') ||
    '/api/call_center'
}

async function apiFetch(base, path, init = {}) {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers || {})
    }
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok, status: res.status, data }
}

/** Detach remote audio elements created for a LiveKit room. */
function detachRoomAudio(room) {
  if (!room) return
  try {
    room.remoteParticipants.forEach((p) => {
      p.audioTrackPublications.forEach((pub) => {
        try { pub.track?.detach()?.forEach((el) => el.remove()) } catch (_) { /* ignore */ }
      })
    })
  } catch (_) { /* ignore */ }
}

/** Current remote playback volume while echo-ducking is active (1 = full). */
let remoteDuckVolume = 1

function setRemotePlaybackVolume(volume) {
  remoteDuckVolume = volume
  document.querySelectorAll('audio[data-call-widget-remote="1"]').forEach((el) => {
    el.volume = volume
  })
}

/** Attach a remote audio track and force play (Chrome blocks autoplay after async accept). */
function attachRemoteAudioTrack(track) {
  if (!track || track.kind !== Track.Kind.Audio) return null
  const el = track.attach()
  el.autoplay = true
  el.playsInline = true
  el.setAttribute('playsinline', 'true')
  el.dataset.callWidgetRemote = '1'
  el.volume = remoteDuckVolume
  document.body.appendChild(el)
  void el.play().catch(() => { /* startAudio() / user gesture will retry */ })
  return el
}

function attachExistingRemoteAudio(room) {
  if (!room) return
  room.remoteParticipants.forEach((p) => {
    p.audioTrackPublications.forEach((pub) => {
      if (pub.track) attachRemoteAudioTrack(pub.track)
    })
  })
}

/**
 * Half-duplex ducking for SIP loopback echo.
 * Softphones cancel locally for the caller; the operator still hears their own
 * voice on the remote track. Mute remote while speaking + hold for RTT.
 */
function startEchoDucking(room) {
  const lp = room?.localParticipant
  if (!lp) return null

  let holdUntil = 0
  let raf = 0
  let currentVol = 1
  let lastAppliedVol = -1

  const markSpeaking = () => {
    holdUntil = performance.now() + ECHO_HOLD_MS
  }

  const onSpeakingChanged = (speaking) => {
    if (speaking) markSpeaking()
  }
  lp.on(ParticipantEvent.IsSpeakingChanged, onSpeakingChanged)
  if (lp.isSpeaking) markSpeaking()

  // Analyser as a faster attack than LiveKit isSpeaking alone.
  let ctx
  let analyser
  let bins
  const micPub = lp.getTrackPublication?.(Track.Source.Microphone)
  const mediaTrack = micPub?.track?.mediaStreamTrack
  if (mediaTrack && typeof AudioContext !== 'undefined') {
    try {
      ctx = new AudioContext()
      const src = ctx.createMediaStreamSource(new MediaStream([mediaTrack]))
      analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.3
      src.connect(analyser)
      bins = new Float32Array(analyser.fftSize)
    } catch (_) {
      ctx = null
      analyser = null
    }
  }

  const tick = () => {
    if (lp.audioLevel >= ECHO_LEVEL_ATTACK) markSpeaking()
    if (analyser && bins) {
      analyser.getFloatTimeDomainData(bins)
      let sumSq = 0
      for (let i = 0; i < bins.length; i += 1) sumSq += bins[i] * bins[i]
      const rms = Math.sqrt(sumSq / bins.length)
      if (rms > 0.02) markSpeaking()
    }

    const shouldDuck = lp.isSpeaking || performance.now() < holdUntil
    const targetVol = shouldDuck ? ECHO_DUCK_VOLUME : 1

    // Instant mute; slow restore so delayed SIP echo tail stays suppressed.
    if (targetVol < currentVol) currentVol = targetVol
    else if (targetVol > currentVol) currentVol = Math.min(1, currentVol + 0.06)

    if (Math.abs(currentVol - lastAppliedVol) > 0.001) {
      lastAppliedVol = currentVol
      setRemotePlaybackVolume(currentVol)
    }

    raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)

  return () => {
    cancelAnimationFrame(raf)
    try { lp.off(ParticipantEvent.IsSpeakingChanged, onSpeakingChanged) } catch (_) { /* ignore */ }
    setRemotePlaybackVolume(1)
    try { ctx?.close() } catch (_) { /* ignore */ }
  }
}

/**
 * Enable mic; returns true if publishing. Callers often grant permission *after*
 * Accept — a single failed setMicrophoneEnabled must be retried.
 */
async function ensureMicrophone(room) {
  if (!room?.localParticipant) return false
  try {
    await room.localParticipant.setMicrophoneEnabled(true, MIC_CAPTURE)
    return room.localParticipant.isMicrophoneEnabled
  } catch (err) {
    console.warn('[CallWidget] microphone enable failed', err)
    return false
  }
}

export function CallWidgetApp({ apiBasePath }) {
  const { callRight } = useWidgetLayout()
  const [offer, setOffer] = useState(null) // { callSessionId, callerContext, timeoutSeconds }
  const [active, setActive] = useState(null) // { callSessionId, room, callerContext }
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [duration, setDuration] = useState(0)
  const [micBlocked, setMicBlocked] = useState(false)
  const [liveLines, setLiveLines] = useState([])
  const [dialLines, setDialLines] = useState([])
  const [dialLineId, setDialLineId] = useState('')
  const [dialDest, setDialDest] = useState('')
  const [dialing, setDialing] = useState(false)
  const roomRef = useRef(null)
  const stopDuckingRef = useRef(null)
  // Bumps on every teardown so a late setActive after room delete cannot stick the UI.
  const joinGenRef = useRef(0)
  const hangupPostedRef = useRef(null)
  const sessionIdRef = useRef(null)
  const talkingRef = useRef(false)

  const clearCallUi = useCallback((sessionId = null) => {
    joinGenRef.current += 1
    talkingRef.current = false
    stopRingtone()
    if (stopDuckingRef.current) {
      try { stopDuckingRef.current() } catch (_) { /* ignore */ }
      stopDuckingRef.current = null
    }
    setLiveLines([])
    setOffer((prev) =>
      sessionId == null || (prev && prev.callSessionId === sessionId) ? null : prev
    )
    setActive((prev) => {
      if (sessionId != null && prev && prev.callSessionId !== sessionId) return prev
      const room = roomRef.current
      roomRef.current = null
      if (room) {
        detachRoomAudio(room)
        try { room.disconnect() } catch (_) { /* ignore */ }
      }
      return null
    })
    setMicBlocked(false)
    setOpen(false)
  }, [])

  useEffect(() => {
    sessionIdRef.current = (active || offer)?.callSessionId ?? null
  }, [active, offer])

  const busyRef = useRef(false)
  useEffect(() => {
    busyRef.current = Boolean(offer || active)
  }, [offer, active])

  useEffect(() => {
    const visible = Boolean(open || offer || active || error)
    const dial = Boolean(open && !offer && !active && !error)
    setWidgetSlot('call', { visible, minimized, dial })
    return () => setWidgetSlot('call', { visible: false, minimized: false, dial: false })
  }, [open, offer, active, error, minimized])

  useEffect(() => {
    const onFocus = () => {
      setMinimized(false)
      setOpen((prev) => (busyRef.current ? true : !prev))
    }
    window.addEventListener('call-center:focus', onFocus)
    return () => window.removeEventListener('call-center:focus', onFocus)
  }, [])

  useEffect(() => {
    const sub = subscribe(
      { channel: 'CallCenter::OperatorChannel' },
      {
        received(data) {
          if (!data || !data.type) return
          if (data.type === 'call_ringing') {
            if (roomRef.current) return
            setOffer({
              callSessionId: data.call_session_id,
              callerContext: data.caller_context,
              timeoutSeconds: data.timeout_seconds || 30,
              roomName: data.room_name
            })
            setMinimized(false)
            setError(null)
            setLiveLines([])
            hangupPostedRef.current = null
            setOpen(true)
          } else if (data.type === 'call_taken') {
            setOffer((prev) =>
              prev && prev.callSessionId === data.call_session_id ? null : prev
            )
          } else if (data.type === 'call_ended') {
            clearCallUi(data.call_session_id)
          } else if (data.type === 'transcript_line' && data.line) {
            if (sessionIdRef.current !== data.call_session_id) return
            setLiveLines((prev) => uniqueTranscript([...prev, data.line]))
          }
        }
      }
    )
    return () => {
      sub.unsubscribe()
      stopRingtone()
      const room = roomRef.current
      roomRef.current = null
      if (room) {
        detachRoomAudio(room)
        try { room.disconnect() } catch (_) { /* ignore */ }
      }
    }
  }, [clearCallUi])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { ok, data } = await apiFetch(apiBasePath, '/call_sessions/dial_options')
      if (cancelled || !ok) return
      const lines = Array.isArray(data.sip_lines) ? data.sip_lines : []
      setDialLines(lines)
      setDialLineId((prev) => prev || (lines[0]?.id ? String(lines[0].id) : ''))
    })()
    return () => { cancelled = true }
  }, [apiBasePath])

  // Inbound offer, or outbound until the callee answers.
  useEffect(() => {
    const outboundRinging = Boolean(active && !active.talking)
    if ((offer && !active) || outboundRinging) {
      playRingtone()
      return () => stopRingtone()
    }
    stopRingtone()
    return undefined
  }, [offer, active])

  useEffect(() => {
    if (!active?.talking) {
      setDuration(0)
      return undefined
    }
    const id = setInterval(() => setDuration((d) => d + 1), 1000)
    return () => clearInterval(id)
  }, [active])

  const postHangupOnce = useCallback(async (sessionId) => {
    if (sessionId == null || hangupPostedRef.current === sessionId) return
    hangupPostedRef.current = sessionId
    try {
      await apiFetch(apiBasePath, `/call_sessions/${sessionId}/hangup`, {
        method: 'POST',
        body: '{}'
      })
    } catch (_) { /* ignore */ }
  }, [apiBasePath])

  const accept = useCallback(async () => {
    if (!offer) return
    setError(null)
    stopRingtone()
    const sessionId = offer.callSessionId
    const callerContext = offer.callerContext
    if (sessionId == null || sessionId === '') {
      setError(t('widget.call_gone', 'This call is no longer available'))
      setOffer(null)
      return
    }
    const { ok, status, data } = await apiFetch(
      apiBasePath,
      `/call_sessions/${sessionId}/accept`,
      { method: 'POST', body: '{}' }
    )
    if (!ok) {
      const gone = status === 404 || status === 410 || data.error === 'call_gone'
      if (gone) {
        setError(t('widget.call_gone', 'This call is no longer available'))
        setOffer(null)
        return
      }
      setError(
        status === 409 || data.error === 'already_taken'
          ? t('widget.already_taken', 'Call already taken')
          : (data.error === 'not_offered'
            ? t('widget.not_offered', 'You were not offered this call')
            : (data.error && data.error !== 'Record not found'
              ? data.error
              : t('widget.accept_failed', 'Failed to accept call')))
      )
      if (status === 409) setOffer(null)
      return
    }

    const joinGen = ++joinGenRef.current
    const room = new Room({
      audioCaptureDefaults: MIC_CAPTURE
    })
    roomRef.current = room

    const onDisconnected = () => {
      // Room already gone (caller hangup / finish / delete_room) — never leave
      // the green "in progress" panel stuck after a late setActive.
      if (stopDuckingRef.current) {
        try { stopDuckingRef.current() } catch (_) { /* ignore */ }
        stopDuckingRef.current = null
      }
      if (roomRef.current === room) roomRef.current = null
      detachRoomAudio(room)
      setActive((prev) => (prev?.room === room || prev?.callSessionId === sessionId ? null : prev))
      setOffer((prev) => (prev?.callSessionId === sessionId ? null : prev))
      setMicBlocked(false)
      // Best-effort hangup if we did not already POST (caller BYE / room delete).
      void postHangupOnce(sessionId)
    }

    const isSipParticipant = (participant) => {
      const identity = String(participant?.identity || '').toLowerCase()
      if (identity.startsWith('sip')) return true
      const kind = String(participant?.kind || '').toLowerCase()
      return kind.includes('sip')
    }

    const onParticipantDisconnected = (participant) => {
      if (!isSipParticipant(participant)) return
      // Caller BYE while operator is in the room — finish the call once.
      void (async () => {
        await postHangupOnce(sessionId)
        clearCallUi(sessionId)
      })()
    }

    room.on(RoomEvent.Disconnected, onDisconnected)
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
    room.on(RoomEvent.TrackSubscribed, (track) => {
      attachRemoteAudioTrack(track)
    })
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      try { track.detach().forEach((el) => el.remove()) } catch (_) { /* ignore */ }
    })

    try {
      // Register Disconnected *before* connect — room can be deleted while
      // signal fallback / mic publish is still in flight.
      // peerConnectionTimeout: safety margin if signal path is slow.
      await room.connect(data.livekit_url, data.livekit_token, {
        peerConnectionTimeout: 45_000
      })
      if (joinGen !== joinGenRef.current || room.state === ConnectionState.Disconnected) {
        detachRoomAudio(room)
        try { room.disconnect() } catch (_) { /* ignore */ }
        setOffer(null)
        setError(t('widget.call_gone', 'This call is no longer available'))
        return
      }

      // Unlock remote playback in the same user-gesture chain as Accept when possible.
      try { await room.startAudio() } catch (_) { /* retry via panel click */ }

      const micOk = await ensureMicrophone(room)
      setMicBlocked(!micOk)

      if (joinGen !== joinGenRef.current || room.state === ConnectionState.Disconnected) {
        detachRoomAudio(room)
        setOffer(null)
        setActive(null)
        setError(t('widget.call_gone', 'This call is no longer available'))
        return
      }

      attachExistingRemoteAudio(room)
      try { await room.startAudio() } catch (_) { /* ignore */ }

      if (stopDuckingRef.current) {
        try { stopDuckingRef.current() } catch (_) { /* ignore */ }
      }
      stopDuckingRef.current = startEchoDucking(room)

      talkingRef.current = true
      setActive({
        callSessionId: sessionId,
        callerContext: data.caller_context || callerContext,
        room,
        talking: true
      })
      setOffer(null)
    } catch (err) {
      console.error(err)
      if (roomRef.current === room) roomRef.current = null
      detachRoomAudio(room)
      try { room.disconnect() } catch (_) { /* ignore */ }
      setOffer(null)
      setActive(null)
      setMicBlocked(false)
      setError(t('widget.connect_failed', 'Failed to connect audio'))
    }
  }, [offer, apiBasePath, clearCallUi, postHangupOnce])

  // If mic was denied at Accept, retry when the browser later grants permission
  // (or when an input device appears). Without this, SIP never hears the operator.
  useEffect(() => {
    if (!active?.room) return undefined
    const room = active.room
    let cancelled = false

    const retryMic = async () => {
      if (cancelled || roomRef.current !== room) return
      if (room.localParticipant?.isMicrophoneEnabled) {
        setMicBlocked(false)
        if (talkingRef.current && !stopDuckingRef.current) {
          stopDuckingRef.current = startEchoDucking(room)
        }
        return
      }
      const ok = await ensureMicrophone(room)
      if (!cancelled) setMicBlocked(!ok)
      try { await room.startAudio() } catch (_) { /* ignore */ }
      if (ok && !cancelled && talkingRef.current) {
        if (stopDuckingRef.current) {
          try { stopDuckingRef.current() } catch (_) { /* ignore */ }
        }
        stopDuckingRef.current = startEchoDucking(room)
      }
    }

    const onDeviceChange = () => { void retryMic() }
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange)

    let permStatus = null
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'microphone' }).then((status) => {
        if (cancelled) return
        permStatus = status
        status.onchange = () => { void retryMic() }
        if (status.state === 'granted') void retryMic()
      }).catch(() => { /* Permissions API unsupported / blocked */ })
    }

    const poll = setInterval(() => { void retryMic() }, 2000)
    void retryMic()

    return () => {
      cancelled = true
      clearInterval(poll)
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange)
      if (permStatus) permStatus.onchange = null
    }
  }, [active])

  const enableMicAndAudio = useCallback(async () => {
    const room = roomRef.current
    if (!room) return
    try { await room.startAudio() } catch (_) { /* ignore */ }
    const ok = await ensureMicrophone(room)
    setMicBlocked(!ok)
    if (ok && talkingRef.current) {
      attachExistingRemoteAudio(room)
      if (stopDuckingRef.current) {
        try { stopDuckingRef.current() } catch (_) { /* ignore */ }
      }
      stopDuckingRef.current = startEchoDucking(room)
    }
  }, [])

  const hangup = useCallback(async () => {
    if (!active) return
    const sessionId = active.callSessionId
    hangupPostedRef.current = sessionId
    try {
      await apiFetch(apiBasePath, `/call_sessions/${sessionId}/hangup`, {
        method: 'POST',
        body: '{}'
      })
    } catch (_) { /* ignore */ }
    clearCallUi(sessionId)
  }, [active, apiBasePath, clearCallUi])

  const decline = useCallback(() => {
    stopRingtone()
    setOffer(null)
    setError(null)
    setOpen(false)
  }, [])

  const placeCall = useCallback(async (event) => {
    event.preventDefault()
    if (active || offer || dialing) return
    if (!dialLineId || !dialDest.trim()) return
    setError(null)
    setDialing(true)
    const { ok, data } = await apiFetch(apiBasePath, '/call_sessions/outbound', {
      method: 'POST',
      body: JSON.stringify({ sip_line_id: Number(dialLineId), destination: dialDest.trim() })
    })
    setDialing(false)
    if (!ok) {
      setError(data.error || t('widget.outbound_failed', 'Outbound call failed'))
      return
    }

    const sessionId = data.session_id
    const callerContext = data.caller_context
    const joinGen = ++joinGenRef.current
    const room = new Room({ audioCaptureDefaults: MIC_CAPTURE })
    roomRef.current = room

    const onDisconnected = () => {
      if (stopDuckingRef.current) {
        try { stopDuckingRef.current() } catch (_) { /* ignore */ }
        stopDuckingRef.current = null
      }
      if (roomRef.current === room) roomRef.current = null
      detachRoomAudio(room)
      setActive((prev) => (prev?.room === room || prev?.callSessionId === sessionId ? null : prev))
      setMicBlocked(false)
      void postHangupOnce(sessionId)
    }
    const isSipParticipant = (participant) => {
      const identity = String(participant?.identity || '').toLowerCase()
      if (identity.startsWith('sip')) return true
      const kind = String(participant?.kind || '').toLowerCase()
      return kind.includes('sip')
    }
    const onParticipantDisconnected = (participant) => {
      if (!isSipParticipant(participant)) return
      void (async () => {
        await postHangupOnce(sessionId)
        clearCallUi(sessionId)
      })()
    }
    const markCalleeAnswered = () => {
      if (talkingRef.current) return
      talkingRef.current = true
      stopRingtone()
      attachExistingRemoteAudio(room)
      if (!stopDuckingRef.current && room.localParticipant?.isMicrophoneEnabled) {
        stopDuckingRef.current = startEchoDucking(room)
      }
      setActive((prev) =>
        prev?.callSessionId === sessionId ? { ...prev, talking: true } : prev
      )
      void apiFetch(apiBasePath, `/call_sessions/${sessionId}/outbound_connected`, {
        method: 'POST',
        body: '{}'
      })
    }
    const onSipMaybeAnswered = (participant) => {
      if (!isSipParticipant(participant)) return
      if (sipCalleeAnswered(participant)) markCalleeAnswered()
    }

    room.on(RoomEvent.Disconnected, onDisconnected)
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
    room.on(RoomEvent.ParticipantConnected, onSipMaybeAnswered)
    room.on(RoomEvent.ParticipantAttributesChanged, (_changed, participant) => {
      onSipMaybeAnswered(participant)
    })
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (talkingRef.current) attachRemoteAudioTrack(track)
    })
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      try { track.detach().forEach((el) => el.remove()) } catch (_) { /* ignore */ }
    })

    try {
      talkingRef.current = false
      await room.connect(data.livekit_url, data.livekit_token, { peerConnectionTimeout: 45_000 })
      if (joinGen !== joinGenRef.current || room.state === ConnectionState.Disconnected) {
        detachRoomAudio(room)
        try { room.disconnect() } catch (_) { /* ignore */ }
        setError(t('widget.call_gone', 'This call is no longer available'))
        return
      }
      try { await room.startAudio() } catch (_) { /* ignore */ }
      const micOk = await ensureMicrophone(room)
      setMicBlocked(!micOk)
      setActive({ callSessionId: sessionId, room, callerContext, talking: talkingRef.current })
      room.remoteParticipants.forEach(onSipMaybeAnswered)
    } catch (err) {
      detachRoomAudio(room)
      try { room.disconnect() } catch (_) { /* ignore */ }
      roomRef.current = null
      setError(t('widget.connect_failed', 'Failed to connect audio'))
      void postHangupOnce(sessionId)
    }
  }, [active, offer, dialing, dialLineId, dialDest, apiBasePath, postHangupOnce, clearCallUi])

  if (!open && !offer && !active && !error) return null

  if (!offer && !active && !error) {
    return (
      <form
        className="call-float-panel"
        onSubmit={placeCall}
        style={{
          position: 'fixed',
          bottom: '0',
          right: `${callRight}px`,
          zIndex: 1065,
          width: '320px',
          background: 'white',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -4px 24px rgba(15, 23, 42, 0.18)',
          padding: '12px 16px 16px',
          fontFamily: 'inherit'
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#6d28d9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('widget.dial', 'Call')}
          <button
            type="button"
            aria-label={t('widget.dismiss', 'Dismiss')}
            style={{ background: 'none', border: 'none', color: '#6d28d9', cursor: 'pointer', padding: '2px 4px' }}
            onClick={() => setOpen(false)}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {dialLines.length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            {t('widget.no_sip_line', 'No active SIP line')}
          </p>
        ) : (
          <>
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>
              {t('widget.from_line', 'From')} <span className="text-danger">*</span>
            </label>
            <select
              className="form-select form-select-sm mb-2"
              required
              value={dialLineId}
              onChange={(e) => setDialLineId(e.target.value)}
            >
              {dialLines.map((line) => (
                <option key={line.id} value={line.id}>{line.e164}</option>
              ))}
            </select>
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>
              {t('widget.dest_number', 'Number to call')} <span className="text-danger">*</span>
            </label>
            <input
              className="form-control form-control-sm mb-2"
              type="tel"
              required
              value={dialDest}
              onChange={(e) => setDialDest(e.target.value)}
              placeholder="+36…"
            />
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={dialing}>
              <i className="bi bi-telephone-outbound-fill me-1" />
              {t('widget.dial', 'Call')}
            </button>
          </>
        )}
      </form>
    )
  }

  const ctx = (active || offer)?.callerContext

  if (minimized && (offer || active)) {
    return (
      <button
        type="button"
        className="call-float-minimized"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: `${callRight}px`,
          zIndex: 1065,
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          border: 'none',
          background: active?.talking ? '#16a34a' : '#6d28d9',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}
        onClick={() => setMinimized(false)}
        title={
          active?.talking
            ? t('widget.call_in_progress', 'Call in progress')
            : active
              ? t('widget.outbound_ringing', 'Ringing…')
              : t('widget.incoming_call', 'Incoming call')
        }
      >
        <i className="bi bi-telephone-fill" />
      </button>
    )
  }

  const formatDuration = (s) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  return (
    <div
      className="call-float-panel"
      style={{
        position: 'fixed',
        bottom: '0',
        right: `${callRight}px`,
        zIndex: 1065,
        width: '360px',
        maxHeight: '70vh',
        background: 'white',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -4px 24px rgba(15, 23, 42, 0.18)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'inherit'
      }}
    >
      <div
        style={{
          background: active?.talking ? '#16a34a' : '#6d28d9',
          color: 'white',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {active?.talking
            ? t('widget.call_in_progress', 'Call in progress')
            : active
              ? t('widget.outbound_ringing', 'Ringing…')
              : t('widget.incoming_call', 'Incoming call')}
        </span>
        <button
          type="button"
          aria-label={t('widget.minimize', 'Minimize')}
          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '2px 4px' }}
          onClick={() => setMinimized(true)}
        >
          <i className="bi bi-dash-lg" />
        </button>
      </div>

      <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
        {error && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              color: '#b91c1c',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '0.85rem',
              marginBottom: '12px'
            }}
          >
            {error}
          </div>
        )}

        <CallerContextView ctx={ctx} liveLines={liveLines} />

        {active?.talking && (
          <div
            style={{
              textAlign: 'center',
              margin: '16px 0 8px',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#16a34a',
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {formatDuration(duration)}
          </div>
        )}
        {active && !active.talking && (
          <div
            style={{
              textAlign: 'center',
              margin: '16px 0 8px',
              fontSize: '1.15rem',
              fontWeight: 600,
              color: '#6d28d9'
            }}
          >
            {t('widget.outbound_ringing', 'Ringing…')}
          </div>
        )}

        <div className="d-flex gap-2 mt-3">
          {offer && !active && (
            <>
              <button type="button" className="btn btn-success flex-grow-1" onClick={accept}>
                <i className="bi bi-telephone-fill me-1" />
                {t('widget.accept', 'Accept')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={decline}>
                {t('widget.dismiss', 'Dismiss')}
              </button>
            </>
          )}
          {active && (
            <div className="d-flex flex-column gap-2 w-100">
              {micBlocked && (
                <button
                  type="button"
                  className="btn btn-warning w-100"
                  onClick={enableMicAndAudio}
                >
                  <i className="bi bi-mic-mute-fill me-2" />
                  {t('widget.enable_mic', 'Enable microphone')}
                </button>
              )}
              <button type="button" className="btn btn-danger w-100" onClick={hangup}>
                <i className="bi bi-telephone-x-fill me-2" />
                {t('widget.end_call', 'End call')}
              </button>
            </div>
          )}
          {!offer && !active && error && (
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={() => setError(null)}
            >
              {t('widget.dismiss', 'Dismiss')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CallerContextView({ ctx, liveLines = [] }) {
  const transcript = useMemo(
    () => uniqueTranscript([
      ...(Array.isArray(ctx?.transcript_preview) ? ctx.transcript_preview : []),
      ...(Array.isArray(liveLines) ? liveLines : [])
    ]),
    [ctx?.transcript_preview, liveLines]
  )

  if (!ctx) {
    return (
      <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
        {t('widget.no_context', 'No caller details')}
      </p>
    )
  }

  const person = ctx.person
  const bc = ctx.building_complex
  const routing = ctx.routing
  const phone = String(ctx.caller_phone_number || '').trim()
  const phoneDisplay = phone && phone !== '+' && phone.toLowerCase() !== 'unknown'
    ? phone
    : t('widget.unknown_caller', 'Unknown caller')

  return (
    <div className="caller-context">
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.3 }}>
          {phoneDisplay}
        </div>
        {person?.name && (
          <div style={{ fontSize: '0.9rem', marginTop: '2px' }}>{person.name}</div>
        )}
        {person?.email && (
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>{person.email}</div>
        )}
      </div>

      {(routing?.person_group?.name || routing?.reason || ctx.ivr_flow?.name || bc?.name) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '12px'
          }}
        >
          {routing?.person_group?.name && (
            <span
              style={{
                display: 'inline-block',
                background: '#f3f4f6',
                color: '#374151',
                borderRadius: '999px',
                padding: '3px 10px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {t('widget.routing_group', 'Person group')}: {routing.person_group.name}
            </span>
          )}
          {!routing?.person_group?.name && routing?.reason === 'building_complex_responsible' && (
            <span
              style={{
                display: 'inline-block',
                background: '#f3f4f6',
                color: '#374151',
                borderRadius: '999px',
                padding: '3px 10px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {t('widget.routing_bc', 'BC responsible')}
            </span>
          )}
          {bc?.name && (
            <span
              style={{
                display: 'inline-block',
                background: '#f3f4f6',
                color: '#374151',
                borderRadius: '999px',
                padding: '3px 10px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {t('widget.building_complex', 'Building complex')}: {bc.name}
            </span>
          )}
          {ctx.ivr_flow?.name && (
            <span
              style={{
                display: 'inline-block',
                background: '#ede9fe',
                color: '#5b21b6',
                borderRadius: '999px',
                padding: '3px 10px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              IVR: {ctx.ivr_flow.name}
            </span>
          )}
        </div>
      )}

      {Array.isArray(ctx.properties) && ctx.properties.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '4px' }}>
            {t('widget.properties', 'Properties')}
          </div>
          <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
            {ctx.properties.slice(0, 5).map((p) => (
              <li key={p.id}>
                {[p.name, p.door_number, p.code].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {transcript.length > 0 && (
        <div style={{ marginBottom: '4px' }}>
          <div className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '6px' }}>
            {t('widget.transcript', 'Recent talk')}
          </div>
          <div
            style={{
              fontSize: '0.85rem',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '10px 12px',
              background: '#f9fafb',
              maxHeight: '140px',
              overflowY: 'auto',
              lineHeight: 1.45
            }}
          >
            {transcript.map((line, i) => (
              <div key={i} style={{ marginBottom: i < transcript.length - 1 ? '8px' : 0 }}>
                <span style={{ fontWeight: 600, color: '#4b5563' }}>
                  {roleLabel(line.role)}
                </span>
                <span style={{ color: '#9ca3af' }}> · </span>
                <span>{line.content}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function mountCallWidget(el, apiBasePath) {
  if (!el) return
  const base = apiBasePath || apiBase(el)
  if (el.__callWidgetMounted) return
  el.__callWidgetMounted = true
  render(<CallWidgetApp apiBasePath={base} />, el)
}

export function unmountCallWidget(el) {
  if (!el) return
  stopRingtone()
  render(null, el)
  delete el.__callWidgetMounted
}
